import { Loader2 } from 'lucide-react';
import { useCallback, useState } from 'react';
import type { AddressInput, AddressVerificationResponse } from '@/api/address';
import { verifyAddress } from '@/api/address';
import { FormControl, FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// US States for dropdown
const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'PR', name: 'Puerto Rico' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'VI', name: 'Virgin Islands' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
];

export interface AddressFormData {
  address1: string;
  address2: string;
  city: string;
  state: string;
  zipCode: string;
}

export type AddressVerificationStatus = 'verified' | 'not_verified' | 'not_checked';

interface AddressFormFieldsProps {
  address: AddressFormData;
  errors: Record<string, string>;
  onChange: (field: keyof AddressFormData, value: string) => void;
  onVerificationResult?: (result: AddressVerificationResponse) => void;
  onVerificationStatusChange?: (status: AddressVerificationStatus) => void;
  disabled?: boolean;
  /** When true, address fields (except line 2) show required asterisks. Defaults to true. */
  required?: boolean;
  /** Pass with accountId to persist verification to account_verifications */
  partyId?: string | null;
  /** Pass with partyId to persist verification to account_verifications */
  accountId?: string | null;
}

export function AddressFormFields({
  address,
  errors,
  onChange,
  onVerificationResult,
  onVerificationStatusChange,
  disabled = false,
  required = true,
  partyId,
  accountId,
}: AddressFormFieldsProps) {
  const [isVerifying, setIsVerifying] = useState(false);

  // Trigger address verification when user blurs from ZIP code field
  const handleZipCodeBlur = useCallback(async () => {
    // Only verify if we have enough data
    if (!address.address1 || !address.city || !address.state || !address.zipCode) {
      return;
    }

    // Validate ZIP code format
    if (!/^\d{5}(-\d{4})?$/.test(address.zipCode)) {
      return;
    }

    setIsVerifying(true);

    try {
      const addressInput: AddressInput = {
        streetAddress: address.address1,
        secondaryAddress: address.address2 || null,
        city: address.city,
        state: address.state,
        zipCode: address.zipCode,
        ...(partyId && { partyId }),
        ...(accountId && { accountId }),
      };

      const result = await verifyAddress(addressInput);

      if (onVerificationResult) {
        onVerificationResult(result);
      }

      if (onVerificationStatusChange) {
        if (result.verified) {
          onVerificationStatusChange('verified');
        } else if (
          result.error?.code === 'NOT_CONFIGURED' ||
          result.error?.code === 'NETWORK_ERROR'
        ) {
          onVerificationStatusChange('not_checked');
        } else {
          onVerificationStatusChange('not_verified');
        }
      }
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: fine for web-app to log verification errors
      console.error('Address verification failed:', error);
      if (onVerificationStatusChange) {
        onVerificationStatusChange('not_checked');
      }
    } finally {
      setIsVerifying(false);
    }
  }, [address, partyId, accountId, onVerificationResult, onVerificationStatusChange]);

  return (
    <div className="space-y-4">
      <FormField label="Street Address" required={required} error={errors.address1}>
        <FormControl>
          <Input
            placeholder="123 Main Street"
            value={address.address1}
            onChange={(e) => onChange('address1', e.target.value)}
            disabled={disabled}
          />
        </FormControl>
      </FormField>

      <FormField label="Apartment, Suite, Unit (Optional)" error={errors.address2}>
        <FormControl>
          <Input
            placeholder="Apt 4B"
            value={address.address2}
            onChange={(e) => onChange('address2', e.target.value)}
            maxLength={200}
            disabled={disabled}
          />
        </FormControl>
      </FormField>

      <div className="grid gap-4 md:grid-cols-2">
        <FormField label="City" required={required} error={errors.city}>
          <FormControl>
            <Input
              placeholder="New York"
              value={address.city}
              onChange={(e) => onChange('city', e.target.value)}
              disabled={disabled}
            />
          </FormControl>
        </FormField>

        <FormField label="State" required={required} error={errors.state}>
          <Select
            value={address.state}
            onValueChange={(value) => onChange('state', value)}
            disabled={disabled}
          >
            <FormControl>
              <SelectTrigger>
                <SelectValue placeholder="Select state" />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {US_STATES.map((state) => (
                <SelectItem key={state.code} value={state.code}>
                  {state.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
      </div>

      <FormField
        label="ZIP Code"
        required={required}
        error={errors.zipCode}
        description="5 digits or 5+4 format (e.g., 10001-1234)"
      >
        <FormControl>
          <Input
            placeholder="10001"
            value={address.zipCode}
            onChange={(e) => {
              // Allow only digits and hyphen
              const value = e.target.value.replace(/[^\d-]/g, '');
              onChange('zipCode', value);
            }}
            onBlur={handleZipCodeBlur}
            maxLength={10}
            disabled={disabled || isVerifying}
          />
        </FormControl>
        <output
          className={
            isVerifying ? 'inline-flex items-center text-sm text-muted-foreground' : 'sr-only'
          }
        >
          {isVerifying ? (
            <>
              <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden="true" />
              Verifying address...
            </>
          ) : null}
        </output>
      </FormField>
    </div>
  );
}

export { US_STATES };
